import { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ButtonSet } from '@trinity/chat-components/components/Modal/ButtonSet';
import { Header } from '@trinity/chat-components/components/Modal/Header';
import { Modal } from '@trinity/chat-components/components/Modal/Modal';
import Button from '@trinity/chat-components/components/presentation/Button/Button';
import styles from './styles/DisplayrDashboardModal.module.css';

type Props = {
  open: boolean;
  onClose: () => void;
  initialDashboardId?: string;
  initialTabName?: string;
  /** Defaults to the create wording; an edit flow passes its own. */
  title?: string;
  updateDisplayrDashboard: (data: {
    dashboardId: string;
    tabName: string;
  }) => Promise<void>;
};

export const DisplayrDashboardModal = ({
  open,
  onClose,
  initialDashboardId = '',
  initialTabName = '',
  title = 'Create Displayr Dashboard',
  updateDisplayrDashboard,
}: Props) => {
  const [value, setValue] = useState<string>(initialDashboardId);
  const [tabName, setTabName] = useState<string>(initialTabName);

  const handleInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  }, []);

  const handleNameInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setTabName(e.target.value);
  }, []);

  const { isPending: isSubmitting, mutateAsync } = useMutation({
    mutationFn: () => {
      const finalTabName = (tabName || '').trim() || 'Displayr Dashboard';
      return updateDisplayrDashboard({
        dashboardId: value,
        tabName: finalTabName,
      });
    },
    onSuccess: () => {
      onClose();
    },
  });

  const handleSubmit = useCallback(() => {
    return mutateAsync();
  }, [mutateAsync]);

  const submitDisabled = useMemo(() => {
    return isSubmitting || !value?.trim();
  }, [isSubmitting, value]);

  return (
    <Modal onClose={onClose} open={open}>
      <Header text={title} />

      <div className={styles.input}>
        <input
          className={styles.inputField}
          onChange={handleInput}
          placeholder="Enter Dashboard ID"
          value={value}
        />
      </div>

      <div className={styles.input}>
        <input
          className={styles.inputField}
          onChange={handleNameInput}
          placeholder="Enter Dashboard Tab Name"
          value={tabName}
        />
      </div>

      <div className={styles.note}>
        * The Dashboard ID can be obtained from the URL of the dashboard in Displayr.
        <br />
        <br />
        https://app.displayr.com/Dashboard?id=
        <span className={styles.id}>6fbbacab-37de-4239-bb75-6623f69476d2</span>
      </div>

      <div className={styles.footer}>
        <ButtonSet>
          <Button variant="brick" color="primary" disabled={submitDisabled} onClick={handleSubmit}>
            Save
          </Button>
        </ButtonSet>
      </div>
    </Modal>
  );
};
